import { Controller } from '@hotwired/stimulus'
import { renderStreamMessage } from '@hotwired/turbo'
import { patch, post } from '@rails/request.js'
import { responseErrors, showFlashMessage } from '../../helpers'
import { elementsForAdjacentWeeks, refreshTurboFrames } from '../../helpers/calendar/turbo_frames'

const DRAG_THRESHOLD = 4
const SNAP_MS = 130

// Drags calendar entries. A single-week card -- moved, or added from the drawer
// -- rides the pointer while a dashed slot opens in the stack where it will
// land; a multi-week bar is its own preview, snapping cell to cell into band
// lanes. Over invalid ground the preview holds its last valid place, and
// releasing commits there. Pointer events and geometry throughout; the grid is
// never touched until the server confirms the drop.
export default class extends Controller {
  static targets = ['updateForm', 'scrollRoot', 'cantMove']
  static values = { cap: { type: Number, default: 3 } }

  disconnect() {
    this.cancel()
    this.finishSnap()
  }

  // Images and links are natively draggable, and a native drag wrests the
  // pointer stream away from this controller mid-grab.
  blockNativeDrag(event) {
    event.preventDefault()
  }

  // Which covered week the grab landed in: a grid bar spans both weeks in one
  // element, so the pointer's half decides; a list segment names its week.
  grabWeeksOf(bar, event, rect) {
    if (!bar) {
      return 0
    }
    if (bar.dataset.segment) {
      return bar.dataset.segment === 'continuation' ? 1 : 0
    }
    return event.clientX - rect.left >= rect.width / 2 ? 1 : 0
  }

  grab(event) {
    if (event.button !== 0 || this.drag || event.target.closest('.undraggable')) {
      return
    }

    const bar = event.target.closest('[data-calendar-entry-two-weeks="true"]')
    const card = bar ? null : event.target.closest('[data-calendar-entry-id]')
    const tile = bar || card ? null : event.target.closest('[data-drop-url]')
    const item = bar || card || tile
    const popoverSlot = card?.closest('[data-popover-week]')
    if (!item || (card && !popoverSlot && !card.closest('[data-calendar--sortable-target="slot"]'))) {
      return
    }

    this.finishSnap()
    const rect = item.getBoundingClientRect()
    // A popover card's origin index never matches a slot: any drop is a move.
    const origin = popoverSlot
      ? this.placementOf(document.getElementById(popoverSlot.dataset.popoverWeek))
      : this.placementOf(item.closest('[id^="week_slot_"]'))
    if (card && origin) {
      origin.home = { parent: card.parentElement, next: card.nextSibling }
      origin.index = popoverSlot ? -1 : this.cardsOf(card.parentElement).indexOf(card)
    }

    this.drag = {
      item,
      mode: bar ? 'bar' : (card ? 'card' : 'add'),
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      grabWeeks: this.grabWeeksOf(bar, event, rect),
      grabOffsetX: event.clientX - rect.left,
      grabOffsetY: event.clientY - rect.top,
      origin,
      proposed: null,
      started: false
    }

    this.onMove = (e) => this.move(e)
    this.onUp = (e) => { if (e.pointerId === this.drag?.pointerId) this.release() }
    this.onCancelEvent = (e) => { if (e.pointerId === this.drag?.pointerId) this.cancel() }
    this.onKey = (e) => { if (e.key === 'Escape') this.cancel({ snapHome: true }) }
    this.onScroll = () => this.reposition()
    this.onStream = () => this.cancel()
    window.addEventListener('pointermove', this.onMove)
    window.addEventListener('pointerup', this.onUp)
    window.addEventListener('pointercancel', this.onCancelEvent)
    window.addEventListener('keydown', this.onKey)
    window.addEventListener('scroll', this.onScroll, { capture: true, passive: true })
    document.addEventListener('turbo:before-stream-render', this.onStream)
  }

  move(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) {
      return
    }

    // Until capture is taken (below, on begin), a pointerup released outside
    // the window never reaches these listeners, so the first buttonless move
    // stands in for it. Where the pointer was let go is unknowable, so the drag
    // snaps home instead of committing a placement the user may have dragged
    // away from. Once captured, buttonless moves are Chrome synthesizing for
    // layout shifting under the pressed pointer -- not a release -- and the
    // captured pointerup is guaranteed to arrive.
    if (event.buttons === 0 && !this.element.hasPointerCapture(event.pointerId)) {
      this.cancel({ snapHome: true })
      return
    }

    if (!this.drag.started) {
      const moved = Math.hypot(event.clientX - this.drag.startX, event.clientY - this.drag.startY)
      if (moved < DRAG_THRESHOLD) {
        return
      }

      this.begin()
    }

    this.lastPointer = { clientX: event.clientX, clientY: event.clientY }
    event.preventDefault()
    // A tile carried back off the calendar is being put down, not dropped:
    // letting go there cancels instead of landing in the last week crossed.
    if (this.drag.mode === 'add' && !this.overCalendar(event)) {
      this.withdraw()
    } else {
      this.propose(this.cellUnder(event), event)
    }
    // After the geometry reads above, so they run on a clean layout.
    if (this.drag.mode !== 'bar') {
      this.followPointer(event)
    }
  }

  overCalendar({ clientX: x, clientY: y }) {
    const rect = this.scrollRootRect
    return !rect || (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom)
  }

  withdraw() {
    if (!this.drag.proposed) {
      return
    }

    this.clearPreview()
    this.drag.proposed = null
    this.drag.previewKey = null
  }

  begin() {
    const { item, mode } = this.drag
    this.drag.started = true
    try { this.element.setPointerCapture(this.drag.pointerId) } catch { }
    // is-dragging is shared with the list view's SortableJS; the pointer flag
    // scopes rules that only this drag wants.
    document.body.classList.add('is-dragging', 'is-pointer-dragging')

    // A palette tile lifts as the pill it becomes on the calendar, from the
    // template rendered beside it; everything else lifts as itself.
    const template = mode === 'add' && item.nextElementSibling?.matches('template[data-drag-ghost]')
      ? item.nextElementSibling
      : null
    const rect = item.getBoundingClientRect()
    this.ghost = (template?.content.firstElementChild ?? item).cloneNode(true)
    this.ghost.removeAttribute('id')
    // The clone must not duplicate the item's lazy menu frame id or load it,
    // nor spin up live tooltip controllers mid-drag.
    this.ghost.querySelectorAll('turbo-frame').forEach(frame => frame.remove())
    this.ghost.querySelectorAll('[data-controller~="tippy"]').forEach(el => el.removeAttribute('data-controller'))
    this.ghost.classList.add('drag-lifted')
    if (!template) {
      this.ghost.style.width = `${rect.width}px`
    }
    this.ghost.style.left = `${rect.left}px`
    this.ghost.style.top = `${rect.top}px`
    document.body.appendChild(this.ghost)

    this.dragPopover = item.closest('[data-tippy-root]')

    this.bandRecords = new Map()
    this.createdBands = []
    this.hiddenCards = []
    this.moreRecords = []
    this.cardSpacer = null
    if (mode !== 'bar') {
      // A card's ghost rides the pointer rather than snapping to cells.
      this.ghost.style.transition = 'none'
    }
    if (mode === 'add') {
      // A palette tile rides centered, and the palette keeps its original.
      const ghostRect = this.ghost.getBoundingClientRect()
      this.drag.grabOffsetX = ghostRect.width / 2
      this.drag.grabOffsetY = ghostRect.height / 2
      this.followPointer({ clientX: this.drag.startX, clientY: this.drag.startY })
    } else {
      this.collapse(item)
    }
    if (mode === 'bar') {
      this.relayout(this.drag.origin, null)
    }
    this.scrollRootRect = this.hasScrollRootTarget ? this.scrollRootTarget.getBoundingClientRect() : null
    this.overGrid = false
    this.autoscrollFrame = requestAnimationFrame(() => this.autoscroll())
  }

  // Nudges the grid container while the pointer rides its edges, then
  // re-proposes at the held pointer so the drag advances into the revealed
  // weeks; the capture-phase scroll listener re-anchors the ghost. The rect is
  // cached at lift and refreshed on scroll, so an idle frame costs arithmetic
  // only.
  autoscroll() {
    if (!this.drag?.started) {
      return
    }

    const rect = this.scrollRootRect
    const puttingDown = this.drag.mode === 'add' && !this.overCalendar(this.lastPointer)
    if (rect && !puttingDown && this.reachedGrid(rect)) {
      const dx = this.edgeSpeed(this.lastPointer.clientX, rect.left, rect.right)
      const dy = this.edgeSpeed(this.lastPointer.clientY, rect.top, rect.bottom)
      if (dx || dy) {
        this.scrollRootTarget.scrollBy(dx, dy)
        this.propose(this.cellUnder(this.lastPointer), this.lastPointer)
      }
    }

    this.autoscrollFrame = requestAnimationFrame(() => this.autoscroll())
  }

  // Edge scrolling waits until the pointer has been over the grid once -- true
  // from the first frame for a card lifted out of a cell, and it keeps
  // overshooting the edge working after. A tile lifted from the sidebar enters
  // through the left edge band, where arming on contact scrolled the weeks away
  // under the pointer, so it waits until the pointer has cleared the bands.
  reachedGrid(rect) {
    const { clientX: x, clientY: y } = this.lastPointer
    const inside = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
    const clear = !this.edgeSpeed(x, rect.left, rect.right) && !this.edgeSpeed(y, rect.top, rect.bottom)
    this.overGrid ||= this.drag.mode === 'add' ? clear : inside

    return this.overGrid
  }

  edgeSpeed(position, low, high, edge = 40, max = 10) {
    if (position < low + edge) {
      return -Math.min(max, Math.ceil(((low + edge - position) / edge) * max))
    }
    if (position > high - edge) {
      return Math.min(max, Math.ceil(((position - (high - edge)) / edge) * max))
    }
    return 0
  }

  followPointer(event) {
    if (!this.ghost) {
      return
    }

    this.ghost.style.left = `${event.clientX - this.drag.grabOffsetX}px`
    this.ghost.style.top = `${event.clientY - this.drag.grabOffsetY}px`
  }

  // Inline style rather than the hidden class: the cards carry their own display
  // utilities, which outrank a class-based display none in the cascade.
  collapse(el) {
    el.style.display = 'none'
  }

  reveal(el) {
    el.style.removeProperty('display')
  }

  collapsed(el) {
    return el.style.display === 'none'
  }

  cellUnder(event) {
    const hit = document.elementFromPoint(event.clientX, event.clientY)
    return hit?.closest('[id^="week_slot_"]')
  }

  bandOf(cell) {
    return cell?.querySelector(':scope > [data-band]')
  }

  placementOf(cell) {
    const data = cell?.querySelector('[data-calendar--sortable-target="slot"]')?.dataset
    if (!data) {
      return null
    }

    return {
      sequence: data.sequence,
      term: data.term,
      week: parseInt(data.termWeek, 10),
      termWeeks: parseInt(cell.dataset.termWeeks, 10),
      date: data.week
    }
  }

  // The preview follows the placement under the cursor, its own included, and
  // over invalid ground stays where it last was, exactly like a lifted object
  // with nowhere new to settle. The affected cells re-lay out as it goes;
  // releasing back home is a cancel, not a move.
  propose(cell, point) {
    if (this.drag.mode === 'bar') {
      this.proposeBar(cell)
    } else {
      this.proposeCard(cell, point)
    }
  }

  proposeBar(cell) {
    const hovered = this.placementOf(cell)
    const start = hovered && hovered.week - this.drag.grabWeeks
    const origin = this.drag.origin
    const valid = hovered && start >= 1 && start + 1 <= hovered.termWeeks

    if (!valid) {
      return
    }

    const key = `${hovered.sequence}:${hovered.term}:${start}`
    if (key === this.drag.previewKey) {
      return
    }

    this.drag.previewKey = key
    this.clearPreview()
    if (!(origin && hovered.sequence === origin.sequence && hovered.term === origin.term)) {
      this.relayout(origin, null)
    }
    this.drag.previewLane = this.relayout(hovered, start)
    this.drag.proposed = this.placementOf(document.getElementById(`week_slot_${hovered.sequence}_${hovered.term}_${start}`))
    this.placeGhost()
  }

  // A card previews as the dashed empty slot it will fill: the stack parts under
  // the pointer and the cap math collapses whatever no longer fits, exactly as
  // the server will render it after the drop. Consecutive moves into the same
  // gap bail before touching the DOM.
  proposeCard(cell, point) {
    if (!cell) {
      const panel = document.elementFromPoint(point.clientX, point.clientY)?.closest('[data-popover-week]')
      if (panel) {
        this.proposePopoverCard(panel, point)
      }
      return
    }

    const hovered = this.placementOf(cell)
    if (!hovered) {
      return
    }

    const slot = cell.querySelector('[data-calendar--sortable-target="slot"]')
    const moreLine = slot.querySelector('[data-more-count]')
    const index = this.cardIndexAt(cell, slot, moreLine, point)
    const key = `${hovered.sequence}:${hovered.term}:${hovered.week}:${index}`
    if (key === this.drag.previewKey) {
      return
    }

    this.openSlot(slot, this.cardsOf(slot)[index] ?? moreLine, key, { ...hovered, index })
    this.capCell(cell)
  }

  // An open week popover doubles as its week's drop list: the gap forms at
  // the pointer among its cards, and the index maps straight onto the week's
  // single positions -- uncapped, unlike the cell beneath it.
  proposePopoverCard(panel, point) {
    const hovered = this.placementOf(document.getElementById(panel.dataset.popoverWeek))
    if (!hovered) {
      return
    }

    const cards = [...panel.querySelectorAll('[data-calendar-entry-id]:not(.undraggable)')]
      .filter(card => card !== this.cardSpacer && !this.collapsed(card))
    const index = cards.filter(card => {
      const rect = card.getBoundingClientRect()
      return rect.top + rect.height / 2 < point.clientY
    }).length
    const key = `popover:${hovered.sequence}:${hovered.term}:${hovered.week}:${index}`
    if (key === this.drag.previewKey) {
      return
    }

    this.openSlot(panel.querySelector('[data-popover-items]'), cards[index] ?? null, key, { ...hovered, index })
  }

  openSlot(list, before, key, proposed) {
    this.drag.previewKey = key
    this.clearPreview()
    this.cardSpacer = document.createElement('div')
    this.cardSpacer.className = 'calendar-card-spacer schedule-item calendar-ghost'
    list.insertBefore(this.cardSpacer, before)
    this.drag.proposed = proposed
  }

  // Where the gap sits: the count of cards whose midpoint the pointer has
  // passed. Once the cell overflows, the last row belongs to "+N more", so the
  // gap clamps to the last slot that stays visible -- the preview and the
  // committed position stay exactly what the server will render.
  cardIndexAt(cell, slot, moreLine, point) {
    const cards = this.cardsOf(slot)
    const index = cards.filter(card => {
      const rect = card.getBoundingClientRect()
      return rect.top + rect.height / 2 < point.clientY
    }).length

    // No "+N more" line means an uncapped cell: the gap goes anywhere.
    if (!moreLine) {
      return index
    }

    const budget = this.budgetOf(cell)
    const total = cards.length + 1 + parseInt(moreLine.dataset.moreCount, 10)
    return total > budget ? Math.min(index, Math.max(budget - 2, 0)) : index
  }

  // The active preview's own spacer is not a card, so probing the gap position
  // mid-preview still counts only the real stack.
  cardsOf(slot) {
    return [...slot.children].filter(child =>
      !child.hasAttribute('data-more-count') && child !== this.cardSpacer && !this.collapsed(child)
    )
  }

  budgetOf(cell) {
    return this.capValue - this.laneCount(cell)
  }

  placeGhost() {
    const rect = this.proposedSpacer()?.getBoundingClientRect()
    if (!rect || !this.ghost) {
      return
    }

    this.ghost.style.left = `${rect.left}px`
    this.ghost.style.top = `${rect.top}px`
  }

  replaceSpacer(spacer, html) {
    spacer.insertAdjacentHTML('beforebegin', html)
    const card = spacer.previousElementSibling
    if (card && this.collapsed(spacer)) {
      this.collapse(card)
    }
    spacer.remove()
  }

  // The tagged spacer is unique to the drag and may sit in the covered week's
  // cell when the continuation half was grabbed, so it is found document-wide.
  proposedSpacer() {
    const p = this.drag?.proposed
    const cell = p && document.getElementById(`week_slot_${p.sequence}_${p.term}_${p.week}`)
    return document.querySelector('[data-preview-spacer]') ?? this.bandOf(cell)?.children[this.drag.previewLane]
  }

  // Scrolling mid-drag moves the cells under the fixed-position ghost, so it
  // re-anchors to its proposed lane -- without the snap ease, which would trail.
  reposition() {
    if (this.scrollRootRect) {
      this.scrollRootRect = this.scrollRootTarget.getBoundingClientRect()
    }
    // A card rides the pointer, which scrolling doesn't move -- and restoring
    // its transition here would hand it the snap ease.
    if (!this.ghost || this.drag?.mode !== 'bar') {
      return
    }

    this.ghost.style.transition = 'none'
    this.placeGhost()
    requestAnimationFrame(() => this.ghost?.style.removeProperty('transition'))
  }

  // Re-lays a whole row's bands to the layout the server would render with the
  // dragged bar at dStart, or gone when null: the same [week, position, id]
  // sort and greedy lane assignment as Sequence#multi_week_lanes, so other bars
  // shift lanes and stale gaps collapse exactly as they will after the drop.
  relayout({ sequence, term }, dStart) {
    const cells = []
    for (let week = 1; ; week++) {
      const cell = document.getElementById(`week_slot_${sequence}_${term}_${week}`)
      if (!cell) {
        break
      }
      cells.push(cell)
    }

    const entries = []
    const draggedId = parseInt(this.drag.item.dataset.calendarEntryId, 10)
    this.previewContinuations = new Map()
    cells.forEach((cell, index) => {
      cell.querySelectorAll('[data-calendar-entry-two-weeks="true"]').forEach(el => {
        const id = parseInt(el.dataset.calendarEntryId, 10)
        // Grabbing either segment lifts the whole entry; its other half is
        // kept aside to draw the covered week of the preview.
        if (id === draggedId) {
          if (el !== this.drag.item) {
            this.drag.siblingEl = el
          }
          return
        }
        // A list entry renders one segment per covered week: the start places
        // it in a lane, the continuation is kept to redraw its covered week.
        if (el.dataset.segment === 'continuation') {
          this.previewContinuations.set(id, el)
          return
        }
        entries.push({ id, week: index + 1, position: parseInt(el.dataset.calendarEntryPosition, 10), el })
      })
    })
    if (dStart) {
      entries.push({ id: draggedId, week: dStart, position: this.previewPosition(sequence, term, dStart), el: null })
    }
    entries.sort((a, b) => a.week - b.week || a.position - b.position || a.id - b.id)

    const laneLast = []
    entries.forEach(entry => {
      let lane = laneLast.findIndex(last => last < entry.week)
      if (lane === -1) {
        lane = laneLast.length
      }
      laneLast[lane] = entry.week + 1
      entry.lane = lane
    })

    cells.forEach((cell, index) => this.applyBand(cell, entries, index + 1))
    return entries.find(entry => !entry.el)?.lane ?? null
  }

  // A drop lands at the band front; releasing over home cancels, keeping the bar's place.
  previewPosition(sequence, term, week) {
    const home = this.drag.origin
    const atHome = sequence === home.sequence && term === home.term && week === home.week

    return atHome ? parseInt(this.drag.item.dataset.calendarEntryPosition, 10) : -1
  }

  applyBand(cell, entries, week) {
    const covering = entries.filter(entry => entry.week === week || entry.week + 1 === week)
    let band = this.bandOf(cell)

    if (band && !this.bandRecords.has(band)) {
      this.bandRecords.set(band, [...band.children])
    }

    if (covering.length) {
      if (!band) {
        band = document.createElement('div')
        band.className = 'calendar-band'
        band.dataset.band = ''
        this.createdBands.push(band)
        cell.prepend(band)
      }

      band.replaceChildren(...this.bandSlots(cell, covering, week))
      this.reveal(band)
    } else if (band) {
      band.replaceChildren()
      this.collapse(band)
    }

    this.capCell(cell)
  }

  // The grid keeps one slot per lane so bands align across its side-by-side
  // columns; the uncapped list stacks weeks vertically and compacts to just
  // the covering entries. The dragged entry's landing slot is tagged so the
  // ghost and the settle find it either way.
  bandSlots(cell, covering, week) {
    const spacerFor = (starter) => {
      const spacer = document.createElement('div')
      spacer.className = 'calendar-band-spacer'
      if (starter && !starter.el) {
        spacer.dataset.previewSpacer = ''
      }
      return spacer
    }

    if (!cell.querySelector('[data-more-count]')) {
      const grabbedStart = this.drag.item.dataset.segment !== 'continuation'
      return covering
        .sort((a, b) => a.lane - b.lane)
        .map(entry => {
          const atStart = entry.week === week
          if (entry.el) {
            return atStart ? entry.el : (this.previewContinuations.get(entry.id) ?? spacerFor(null))
          }

          // The dragged entry: the grabbed half rides as the ghost over the
          // tagged spacer, and its other half draws the covered week.
          return atStart === grabbedStart ? spacerFor(entry) : (this.drag.siblingEl ?? spacerFor(null))
        })
    }

    const lanes = Math.max(...covering.map(entry => entry.lane)) + 1
    return Array.from({ length: lanes }, (_, lane) => {
      const starter = covering.find(entry => entry.lane === lane && entry.week === week)
      return starter?.el ?? spacerFor(starter)
    })
  }

  // Reconciles the singles region with the shrunken budget the same way the
  // server will after the drop: rows past the cap collapse into "+N more",
  // revealing the zero-count line the server always renders. Mirrors
  // CalendarHelper#week_cell_singles_budget / #week_cell_more_count -- change
  // them together.
  capCell(cell) {
    const slot = cell.querySelector('[data-calendar--sortable-target="slot"]')
    const moreLine = slot.querySelector('[data-more-count]')
    if (!moreLine) {
      return
    }

    const budget = this.budgetOf(cell)
    const cards = [...slot.children].filter(child => child !== moreLine && !this.collapsed(child))
    const total = cards.length + parseInt(moreLine.dataset.moreCount, 10)
    const target = Math.max(budget - 1, 0)
    if (total <= budget || cards.length <= target) {
      return
    }

    // The gap counts against the budget like the card it stands for, so where
    // the band leaves no room it collapses too and the drop reads as the
    // "+N more" count going up -- which is what the server will render.
    cards.slice(target).forEach(card => {
      this.collapse(card)
      if (card !== this.cardSpacer) {
        this.hiddenCards.push(card)
      }
    })

    this.moreRecords.push({
      moreLine,
      text: moreLine.textContent,
      count: moreLine.dataset.moreCount,
      hidden: moreLine.classList.contains('hidden')
    })
    const count = total - target
    moreLine.dataset.moreCount = count
    moreLine.textContent = moreLine.textContent.replace(/\d+/, count)
    moreLine.classList.remove('hidden')
  }

  clearPreview() {
    this.undoPreview(this.takePreview())
  }

  // The undo data belongs to the drag that recorded it. A rollback runs after
  // the drag is torn down, by which point a new one may have taken the
  // controller's records over, so the rollback takes its own copy first.
  takePreview() {
    const preview = {
      cardSpacer: this.cardSpacer,
      createdBands: this.createdBands ?? [],
      bandRecords: this.bandRecords ?? new Map(),
      hiddenCards: this.hiddenCards ?? [],
      moreRecords: this.moreRecords ?? []
    }
    this.cardSpacer = null
    this.createdBands = []
    this.bandRecords = new Map()
    this.hiddenCards = []
    this.moreRecords = []
    return preview
  }

  undoPreview({ cardSpacer, createdBands, bandRecords, hiddenCards, moreRecords }) {
    cardSpacer?.remove()
    createdBands.forEach(band => band.remove())
    bandRecords.forEach((children, band) => {
      band.replaceChildren(...children)
      this.reveal(band)
    })
    hiddenCards.forEach(card => this.reveal(card))
    moreRecords.forEach(({ moreLine, text, count, hidden }) => {
      moreLine.textContent = text
      moreLine.dataset.moreCount = count
      moreLine.classList.toggle('hidden', hidden)
    })
  }

  laneCount(cell) {
    const band = this.bandOf(cell)
    if (!band || this.collapsed(band)) {
      return 0
    }

    return [...band.children].filter(child => !this.collapsed(child)).length
  }

  // The release is optimistic: the preview is already the exact layout the
  // server will render, so the item materialises in place and the redraw
  // confirms it -- no rollback flicker while the request runs. A refused move
  // restores the pre-drag layout from the same records.
  async release() {
    const drag = this.drag
    if (!drag) {
      return
    }

    if (!drag.started || !drag.proposed) {
      this.cancel({ snapHome: drag.started })
      return
    }

    if (drag.mode === 'add') {
      await this.commitAdd(drag)
      return
    }

    const { item, mode, origin, proposed, previewLane } = drag
    const ghost = this.ghost
    const spacer = this.cardSpacer
    const sameWeek = origin &&
      proposed.sequence === origin.sequence && proposed.term === origin.term && proposed.week === origin.week

    if (item.dataset.answered && !sameWeek) {
      this.cancel({ snapHome: true })
      document.getElementById('calendar_toast').replaceChildren(this.cantMoveTarget.content.cloneNode(true))
      return
    }

    const reorderedInPopover = this.dragPopover && sameWeek
    // The settled list already matches what the server will store, so the
    // popover rides out the redraw that replaces its cell instead of dying
    // with it -- the reorder never visibly closes it.
    if (reorderedInPopover) {
      this.dragPopover.setAttribute('data-hold-through-redraw', '')
    }
    const heldPopover = this.dragPopover
    this.teardown()

    const homeAgain = sameWeek && (mode === 'bar' || proposed.index === origin.index)
    if (homeAgain) {
      ghost.remove()
      this.restore(item)
      return
    }

    // A check-in dropped on another week goes home while a modal asks whether
    // the rest of its series moves too.
    const { moveUrl } = item.dataset
    if (moveUrl && !sameWeek) {
      ghost.remove()
      this.restore(item)
      const url = new URL(moveUrl, window.location.origin)
      url.search = new URLSearchParams({ week_start_date: proposed.date, sequence_id: proposed.sequence, position: proposed.index ?? 0 })
      document.getElementById('modal')?.setAttribute('src', url)
      return
    }

    this.holdQuiet()
    if (mode === 'bar') {
      this.settle(item, proposed, previewLane)
    } else {
      // A cap-collapsed spacer stands for a card behind "+N more": the card
      // lands collapsed too, and the row keeps its height.
      const behindCap = this.collapsed(spacer)
      spacer.replaceWith(item)
      if (behindCap) {
        this.collapse(item)
      } else {
        this.reveal(item)
      }
    }
    ghost.remove()
    const preview = this.takePreview()

    const form = this.updateFormTarget
    this.populateForm(form, {
      week_start_date: proposed.date,
      sequence_id: proposed.sequence,
      calendar_entry_id: item.dataset.calendarEntryId,
      position: mode === 'card' ? proposed.index : 0
    })

    try {
      const response = await patch(form.action, { body: new FormData(form), responseKind: 'turbo-stream' })
      if (!response.ok) {
        heldPopover?.removeAttribute('data-hold-through-redraw')
        this.rollBack(item, preview, origin?.home)
      }
    } catch {
      heldPopover?.removeAttribute('data-hold-through-redraw')
      this.rollBack(item, preview, origin?.home)
      showFlashMessage('Network error. Please try again.')
    } finally {
      this.releaseQuiet()
    }
  }

  // The pointer rests on whatever it just dropped, so hover and transitions
  // stay suppressed until the server's redraw has landed -- otherwise the drop
  // flickers hover, base, hover across the swap.
  holdQuiet() {
    document.body.classList.add('is-dragging', 'is-pointer-dragging')
  }

  releaseQuiet() {
    setTimeout(() => {
      if (!this.drag) {
        document.body.classList.remove('is-dragging', 'is-pointer-dragging')
      }
    }, 100)
  }

  // The spacer, already styled as the dashed slot, stays put as the pending
  // placeholder and the server's card replaces it in place; the palette keeps
  // its original throughout. The cap preview stays -- it already matches what
  // the server will store.
  async commitAdd({ item, proposed }) {
    const ghost = this.ghost
    const spacer = this.cardSpacer
    this.cardSpacer = null
    this.teardown()
    this.holdQuiet()
    ghost.remove()

    try {
      const response = await post(item.dataset.dropUrl, {
        body: {
          week_start_date: proposed.date,
          sequence_id: proposed.sequence,
          position: proposed.index,
          recommendation_id: item.dataset.recommendationId || ''
        }
      })
      if (response.ok) {
        const data = await response.json
        // The card inherits the gap's place in the cell, overflow included: a
        // gap the cap collapsed stands for a card the server renders behind
        // "+N more", and nothing redraws this cell to correct it.
        if (data.html) this.replaceSpacer(spacer, data.html)
        if (data.turbo_streams) renderStreamMessage(data.turbo_streams)
        refreshTurboFrames(elementsForAdjacentWeeks(proposed.sequence, proposed.term, proposed.week))
        // Dropping the cell's cached popover anchor makes the next open refetch.
        document.getElementById(`week_popover_${proposed.sequence}_${proposed.term}_${proposed.week}`)
          ?.closest('[aria-hidden]')?.remove()
        return
      }
      // A drop they could pay to make legal opens the pitch instead of a flash.
      const data = await responseErrors(response)
      const modal = data?.upsell && document.getElementById('modal')
      if (modal) modal.src = data.upsell
      else showFlashMessage(data?.errors?.join(', ') || 'Failed to add. Please try again.')
      spacer.remove()
      this.clearPreview()
    } catch {
      showFlashMessage('Network error. Please try again.')
      spacer.remove()
      this.clearPreview()
    } finally {
      this.releaseQuiet()
    }
  }

  populateForm(form, fields) {
    Object.entries(fields).forEach(([name, value]) => {
      const field = form.querySelector(`[name="${name}"]`)
      if (field) field.value = value
    })
  }

  settle(bar, proposed, lane) {
    const cell = document.getElementById(`week_slot_${proposed.sequence}_${proposed.term}_${proposed.week}`)
    const spacer = document.querySelector('[data-preview-spacer]') ?? this.bandOf(cell)?.children[lane]
    if (!spacer) {
      return
    }

    spacer.replaceWith(bar)
    this.reveal(bar)
  }

  // A settled card has left its origin spot, so a refused move walks it back to
  // the sibling it sat before; a bar comes back through the band records.
  restore(item, home = null) {
    this.clearPreview()
    if (home) {
      home.parent.insertBefore(item, home.next)
    }
    this.reveal(item)
  }

  // Undoes a drop the server refused. A drag started while the request was in
  // flight is laying out the same cells, so it is cancelled first -- as any
  // incoming stream would do -- leaving the row back at the state this drag
  // recorded before its own preview is undone on top.
  rollBack(item, preview, home = null) {
    this.cancel()
    this.undoPreview(preview)
    if (home) {
      home.parent.insertBefore(item, home.next)
    }
    this.reveal(item)
  }

  cancel({ snapHome = false } = {}) {
    if (!this.drag) {
      return
    }

    const { item, started, mode } = this.drag
    const ghost = this.ghost
    this.teardown()
    if (started) {
      this.clearPreview()
    }

    // A drawer tile never left the drawer -- its ghost was a copy -- so a
    // cancelled one just lets go of the copy instead of flying it home.
    if (ghost && snapHome && mode !== 'add') {
      this.reveal(item)
      item.style.visibility = 'hidden'
      const home = item.getBoundingClientRect()
      // A card rode the pointer without easing; it flies home with the snap.
      ghost.style.removeProperty('transition')
      ghost.style.left = `${home.left}px`
      ghost.style.top = `${home.top}px`
      this.snap = { ghost, item }
      this.snapTimer = setTimeout(() => this.finishSnap(), SNAP_MS)
    } else {
      ghost?.remove()
      this.reveal(item)
    }
  }

  teardown() {
    if (this.drag?.started) {
      this.squelchNextClick()
    }
    if (this.drag) {
      try { this.element.releasePointerCapture(this.drag.pointerId) } catch { }
    }
    this.dragPopover = null
    this.drag = null
    this.ghost = null
    cancelAnimationFrame(this.autoscrollFrame)
    document.body.classList.remove('is-dragging', 'is-pointer-dragging')
    window.removeEventListener('pointermove', this.onMove)
    window.removeEventListener('pointerup', this.onUp)
    window.removeEventListener('pointercancel', this.onCancelEvent)
    window.removeEventListener('keydown', this.onKey)
    window.removeEventListener('scroll', this.onScroll, { capture: true })
    document.removeEventListener('turbo:before-stream-render', this.onStream)
  }

  // A drag that actually moved must not double as a click: the browser still
  // dispatches one on the release target, which would follow a link or open a
  // menu inside the dragged element.
  squelchNextClick() {
    const squelch = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }
    window.addEventListener('click', squelch, { capture: true, once: true })
    setTimeout(() => window.removeEventListener('click', squelch, { capture: true }), 0)
  }

  // The snap-home animation finishes on its own clock; a new grab or a
  // disconnect settles it early so the pending timeout cannot reveal an item
  // mid-drag.
  finishSnap() {
    if (!this.snap) {
      return
    }

    clearTimeout(this.snapTimer)
    this.snap.ghost.remove()
    this.snap.item.style.removeProperty('visibility')
    this.snap = null
  }
}
